import React from "react";
import { Clock, AlertCircle, User, Eye, FileText, Shield, LogOut } from "lucide-react";

import { usePageNavigate } from "../hooks/usePageNavigate";

const STATUS_INFO = {
  registered: { label: "Account Registered", color: "text-blue-600", bg: "bg-blue-50 border-blue-200",
    message: "Your account has been created. Please complete your profile to begin the review process." },
  conditional_access: { label: "Conditional Access", color: "text-amber-600", bg: "bg-amber-50 border-amber-200",
    message: "You have limited access while your account is under preliminary review. You can view opportunities and complete your profile." },
  under_review: { label: "Under Review", color: "text-purple-600", bg: "bg-purple-50 border-purple-200",
    message: "Your account is being reviewed by our internal team. You'll be notified once a decision is made." },
  rejected: { label: "Application Declined", color: "text-red-600", bg: "bg-red-50 border-red-200",
    message: "Your application was not approved at this time. Please contact support for more information." },
  suspended: { label: "Account Suspended", color: "text-red-600", bg: "bg-red-50 border-red-200",
    message: "Your account has been temporarily suspended. Please contact support." },
};

const ROLE_INFO = {
  investor: {
    title: "Investor Portal",
    conditional_features: ["View available investment opportunities", "Complete your investor profile", "Learn how the platform works"],
    gated_features: ["Commit capital to deals", "Access detailed deal financials", "View portfolio and distributions"],
  },
  lender: {
    title: "Lender Portal",
    conditional_features: ["View deal pipeline", "Complete your lender profile", "Learn about our lending process"],
    gated_features: ["Submit term sheets", "Access deal underwriting details", "View portfolio tracking"],
  },
};

export default function ConditionalAccessView({ role, user, onLogout }) {
  const { navigateTo } = usePageNavigate();
  const approvalStatus = user?.approval_status || "conditional_access";
  const statusInfo = STATUS_INFO[approvalStatus] || STATUS_INFO.conditional_access;
  const roleInfo = ROLE_INFO[role] || ROLE_INFO.investor;

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-12" data-testid="conditional-access-view">
      <div className="w-full max-w-lg space-y-6">
        {/* Status Banner */}
        <div className={`p-5 rounded-2xl border ${statusInfo.bg}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl ${statusInfo.bg} flex items-center justify-center`}>
              <Clock className={`h-5 w-5 ${statusInfo.color}`} />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${statusInfo.color}`}>{statusInfo.label}</h2>
              <p className="text-xs text-[#666]">{roleInfo.title}</p>
            </div>
          </div>
          <p className="text-sm text-[#444] leading-relaxed">{statusInfo.message}</p>
        </div>

        {/* Welcome */}
        <div className="p-5 rounded-2xl bg-white border border-[#e8e0d0]">
          <div className="flex items-center gap-2 mb-3">
            <User className="h-4 w-4 text-[#888]" />
            <span className="text-sm font-bold text-[#333]">Welcome, {user?.full_name || "User"}</span>
          </div>
          <p className="text-xs text-[#666]">
            You're signed in as a <strong>{role}</strong>. While your account is being reviewed,
            you have conditional access to certain features.
          </p>
        </div>

        {/* What You CAN Do */}
        <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-200">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-bold text-emerald-800">What You Can Do</span>
          </div>
          <ul className="space-y-2">
            {roleInfo.conditional_features.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-emerald-700">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* What Requires Approval */}
        <div className="p-5 rounded-2xl bg-[#faf7f2] border border-[#e8e0d0]">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-4 w-4 text-[#888]" />
            <span className="text-sm font-bold text-[#333]">Requires Full Approval</span>
          </div>
          <ul className="space-y-2">
            {roleInfo.gated_features.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-[#888]">
                <AlertCircle className="h-3 w-3 text-[#bbb] shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button onClick={() => navigateTo("profile")} data-testid="conditional-go-profile"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-600 transition-colors">
            <FileText className="h-4 w-4" /> Complete Profile
          </button>
          <button onClick={onLogout} data-testid="conditional-logout"
            className="px-4 py-3 rounded-xl border border-[#e8e0d0] text-[#666] text-sm font-semibold hover:bg-[#faf7f2] transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
